import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdSync } from "react-icons/md";
import { TbHelpCircle, TbSettings } from "react-icons/tb";
import { Link } from "react-router-dom";
import { SyncHowItWorks, hasSeenSyncGuide, markSyncGuideSeen } from "./SyncHowItWorks";
import PageHeader from "src/components/PageHeader/PageHeader";
import { PraxisSoftwareSyncControls } from "src/components/PraxisSoftwareSync/PraxisSoftwareSyncBody";
import { PraxisSoftwareSyncLogTable } from "src/components/PraxisSoftwareSync/PraxisSoftwareSyncLogTable";
import { usePraxisSoftwareSync } from "src/context/PraxisSoftwareSyncContext";
import { DeviceManagementSection, useCanManageDevices } from "./DeviceManagementSection";
import { BrowserUnsupportedNotice } from "./BrowserUnsupportedNotice";
import { SyncMethodSwitch, useSyncMethod, type SyncMethod, type SyncMethodStatus } from "./SyncMethodSwitch";
import { listDevices } from "src/services/deviceService";
import { getPraxisSoftwareSyncSettings } from "src/services/agentSyncService";
import { apiRequest } from "src/services/apiClient";
import { AgentAttentionSection } from "./AgentAttentionSection";
import type { GuideJumpTarget } from "./guide/SyncGuide";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";
import "src/styles/tables.scss";
import "src/pages/Settings/settings.scss";
import "./praxisSoftwareSyncPage.scss";

/** Standalone top-level destination for this feature — same relationship to
 *  PraxisSoftwareSyncControls that Bank.tsx has to FinApiConnectionSettings.tsx's
 *  controls: a persistent, always-reachable-from-the-header page, not just a Settings
 *  sub-section. Built directly off the earlier finding that a dashboard-only card was
 *  too easy to miss (buried below the fold) and that Bank itself already sets this exact
 *  precedent (Settings section + dashboard card + a real top-level route).
 *
 *  Layout: connection controls stay in a compact card (a toggle and two folder rows
 *  don't need the full page width) stacked above a full-width activity table, not
 *  side by side with it — an earlier side-by-side version put a short card next to a
 *  tall table, which left a large empty gap under the short column once the table grew
 *  past a few rows. Stacking keeps both the width fix (table isn't squeezed) and a
 *  layout that stays balanced regardless of how long the activity list gets. */
export default function PraxisSoftwareSyncPage() {
    const { t } = useTranslation();
    const { supported, connection, lastSyncAt, syncLog, clearSyncLog } = usePraxisSoftwareSync();
    const [method, setMethod] = useSyncMethod(supported);
    const canManageDevices = useCanManageDevices();

    // Counts as reviewed on the "Getting Started" checklist, like a Settings section.
    useEffect(() => {
        apiRequest("/onboarding/progress/visit", {
            method: "POST",
            body: JSON.stringify({ key: "praxis-software-sync" }),
        }, { "Content-Type": "application/json" }).catch(() => {});
    }, []);

    // The how-it-works / setup guide popup, opened on the method it should start with. It opens by itself the
    // first time this browser lands on a method (a browser method this browser can't run is skipped — the
    // unsupported notice is the useful thing there), and closing it marks every method it showed as seen.
    const [howMethod, setHowMethod] = useState<SyncMethod | null>(null);
    useEffect(() => {
        if (method === "browser" && !supported) return;
        if (!hasSeenSyncGuide(method)) setHowMethod(method);
    }, [method, supported]);
    const closeHow = (viewed: SyncMethod[]) => {
        markSyncGuideSeen(viewed);
        setHowMethod(null);
    };

    // Active desktop-app devices, for the status on the method tile. Only users who can manage devices can
    // list them; refreshed on window focus because enrolling happens on another PC.
    const [deviceCounts, setDeviceCounts] = useState<{ valid: number; connected: number } | null>(null);
    useEffect(() => {
        if (!canManageDevices) return;
        let cancelled = false;
        const load = () => {
            listDevices()
                .then((d) => {
                    if (cancelled) return;
                    const valid = d.filter((x) => x.status === "active" || x.status === "expiring_soon");
                    setDeviceCounts({ valid: valid.length, connected: valid.filter((x) => x.connected).length });
                })
                .catch(() => { if (!cancelled) setDeviceCounts(null); });
        };
        load();
        window.addEventListener("focus", load);
        return () => { cancelled = true; window.removeEventListener("focus", load); };
    }, [canManageDevices]);

    // "Pause desktop agent sync" (Settings → Praxis Software Sync) — worth showing here regardless
    // of who can flip it back off, so nobody stares at a silent agent wondering why nothing happens.
    const [syncPaused, setSyncPaused] = useState(false);
    useEffect(() => {
        let cancelled = false;
        getPraxisSoftwareSyncSettings().then((s) => { if (!cancelled) setSyncPaused(s.syncPaused); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const anyFolderConnected = connection.gdt === "connected" || connection.dat === "connected";
    const methodStatus: Record<"browser" | "agent", SyncMethodStatus> = {
        browser: !supported
            ? { tone: "warn", label: t("syncMethod.status.unsupported") }
            : connection.gdt === "checking" || connection.dat === "checking"
                ? null
                : anyFolderConnected
                    ? { tone: "ok", label: t("syncMethod.status.folderConnected") }
                    : { tone: "idle", label: t("syncMethod.status.notSetUp") },
        // "Connected" = an agent has actually checked in recently; enrolled-but-silent devices read as "Not connected".
        // Paused overrides every other reading — it's the one thing that explains "why is nothing happening".
        agent: syncPaused
            ? { tone: "warn", label: t("syncMethod.status.paused") }
            : deviceCounts === null
                ? null
                : deviceCounts.connected > 0
                    ? { tone: "ok", label: t("syncMethod.status.devicesConnected", { count: deviceCounts.connected }) }
                    : deviceCounts.valid > 0
                        ? { tone: "warn", label: t("syncMethod.status.notConnected") }
                        : { tone: "idle", label: t("syncMethod.status.notSetUp") },
    };

    // A guide step's "go to …" link: close the popup on that method and bring its section into view.
    const handleGuideJump = (m: SyncMethod, target: GuideJumpTarget) => {
        closeHow([m]);
        setMethod(m);
        const id = target === "devices" ? "sync-devices" : "sync-folders";
        requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    };

    const [showClearLogConfirm, setShowClearLogConfirm] = useState(false);
    const handleClearLog = () => {
        setShowClearLogConfirm(false);
        void clearSyncLog();
    };

    return (
        <div className="page pvs-sync-page">
            {showClearLogConfirm && (
                <ConfirmPopup
                    message={t("praxisSoftwareSync.confirmClearLog")}
                    onConfirm={handleClearLog}
                    onCancel={() => setShowClearLogConfirm(false)}
                />
            )}
            <PageHeader
                introOnly
                eyebrow={t("nav.praxisSoftwareSync")}
                eyebrowIcon={<MdSync />}
                title={t("praxisSoftwareSync.title")}
                secondaryAction={
                    <>
                        <button type="button" className="page-header__cta page-header__cta--outline" onClick={() => setHowMethod(method)}>
                            <TbHelpCircle /> {t("syncHowItWorks.button")}
                        </button>
                        <Link to="/settings?section=praxis-software-sync" className="page-header__cta page-header__cta--outline page-header__cta--settings">
                            <TbSettings /> {t("praxisSoftwareSync.openSettings")}
                        </Link>
                    </>
                }
                lead={
                    method === "agent"
                        ? t("syncMethod.leadAgent")
                        : supported
                            ? (lastSyncAt
                                ? t("praxisSoftwareSync.pageLeadConnected", { datetime: new Date(lastSyncAt).toLocaleString("de-DE") })
                                : t("praxisSoftwareSync.description"))
                            : t("syncMethod.unsupportedBrowser")
                }
            />

            {howMethod && <SyncHowItWorks initial={howMethod} onClose={closeHow} onJump={handleGuideJump} />}

            <div className="pvs-sync-page__layout">
                {/* Patient sync is one method or the other: the page shows only the chosen one. */}
                <SyncMethodSwitch method={method} onChange={setMethod} status={methodStatus} />

                <div className="pvs-sync-page__panel">
                    {method === "agent" ? (
                        <>
                            {syncPaused && (
                                <p className="pvs-sync-page__note pvs-sync-page__note--warn">{t("syncMethod.pausedBanner")}</p>
                            )}

                            {/* Desktop-agent devices. Doctor/Admin (or settings access) only; works in any browser. */}
                            <div id="sync-devices" className="pvs-sync-page__anchor">
                                {canManageDevices ? <DeviceManagementSection /> : (
                                    <p className="pvs-sync-page__note">{t("syncMethod.agentNeedsAdmin")}</p>
                                )}
                            </div>

                            {/* Undo of recent agent imports + imports the agent held back as ambiguous — only
                                shown when there is something to act on. */}
                            <AgentAttentionSection />

                            <p className="pvs-sync-page__note">
                                {t("syncMethod.invoiceNote")}{" "}
                                <button type="button" className="pvs-sync-page__note-link" onClick={() => setMethod("browser")}>
                                    {t("syncMethod.invoiceNoteAction")}
                                </button>
                            </p>
                        </>
                    ) : (
                        <>
                            {supported ? (
                                <div id="sync-folders" className="settings-section pvs-sync-page__controls pvs-sync-page__anchor">
                                    <div className="settings-section__body body-pvs-sync">
                                        <PraxisSoftwareSyncControls />
                                    </div>
                                </div>
                            ) : (
                                <div id="sync-folders" className="pvs-sync-page__anchor">
                                    <BrowserUnsupportedNotice onUseApp={() => setMethod("agent")} />
                                </div>
                            )}

                            {supported && (
                                <div className="pvs-sync-page__log">
                                    <div className="pvs-sync-page__log-header">
                                        <h2 className="pvs-sync-page__log-title">{t("praxisSoftwareSync.activityTitle")}</h2>
                                        {syncLog.length > 0 && (
                                            <button type="button" className="pvs-sync-clear-btn" onClick={() => setShowClearLogConfirm(true)}>
                                                {t("praxisSoftwareSync.clearLog")}
                                            </button>
                                        )}
                                    </div>
                                    <PraxisSoftwareSyncLogTable />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
